import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ThemeContext } from 'styled-components';

import * as selectors from 'store/services/chat/selectors';
import * as session_selectors from 'store/services/session/selectors';
import * as actions from 'store/services/chat/actions';
import { hasError } from 'store/services/errors';
import { useNotifications } from 'hooks/useNotifications';
import { getStorageItem, setStorageItem } from 'services/storage';
import { STORAGE_KEY } from 'constants/browserStorageKeys';
import ChatWidget from './ChatWidget';
import BubbleWidget from './BubbleWidget';
import {
  getSessionStorage,
  removeSessionStorage,
} from 'services/sessionStorage';
import { ReopenMinimizedChatState } from 'types';

export function Chat(props) {
  const {
    isOpen,
    isLoaded,
    isMinimized,
    isPreviewMode,
    startChat,
    notificationsConfig,
    shouldReopenMinimizedChat,
  } = props;
  const [isFormClosed, closeForm] = React.useState(false);
  const theme = React.useContext(ThemeContext);
  const { withTitle, withSound, audioSrc } = notificationsConfig;
  const { unreadMessages, playSilentSound } = useNotifications({
    withTitle,
    withSound,
    isMinimized,
    isOpen,
    audioSrc,
    messages: props.thread,
  });
  const effectiveUnreadMessages = isPreviewMode ? 1 : unreadMessages;

  const newProps = {
    ...props,
    unreadMessages: effectiveUnreadMessages,
    playSilentSound,
    theme,
    isFormClosed,
  };

  /*
   * Listen localStorage events to control chat instances behavior in other tabs.
   */
  const listenLocalStorage = ({ key, newValue, oldValue }: StorageEvent) => {
    const {
      WELCOME_FORM,
      REOPEN_FEEDBACK,
      SESSION_ID,
      CLOSE_FEEDBACK,
      MINIMIZED_CHAT,
      TAB_SESSION_ID,
    } = STORAGE_KEY;

    closeForm(false);

    // Automatically open all chat boxes when welcome form configured or feedback form should be reopened
    if (
      (key === WELCOME_FORM && newValue === 'false') ||
      (key === REOPEN_FEEDBACK && oldValue === 'false' && newValue === 'true')
    ) {
      props.openChatBox(true);
    }

    // Automatically close all chat instances when user closes one
    if (
      (key === SESSION_ID && oldValue && !newValue) ||
      (key === REOPEN_FEEDBACK && oldValue === 'true' && newValue === 'false')
    ) {
      props.closeChatBox();
    }

    if (
      key === MINIMIZED_CHAT &&
      !getSessionStorage(STORAGE_KEY.TAB_SESSION_ID)
    ) {
      if (!isOpen && oldValue && newValue === 'false') {
        props.openChat();
      } else {
        props.minimizeChatBox(JSON.parse(newValue));
      }
    }

    if (key === TAB_SESSION_ID && oldValue !== newValue) {
      removeSessionStorage(TAB_SESSION_ID);
    }

    // Automatically open all chat instances when user opens one
    if (key === SESSION_ID && !oldValue && newValue) {
      props.openChat();
    }

    if (
      (key === CLOSE_FEEDBACK || key === WELCOME_FORM) &&
      newValue === 'true'
    ) {
      closeForm(true);
    }
  };

  const restoreConnection = () => {
    props.restoreConnection();
  };

  useEffect(() => {
    if (isPreviewMode && !isLoaded) {
      startChat();
      return;
    }
    if (isOpen && !isLoaded) {
      startChat();
    }
  }, []);

  useEffect(() => {
    const shouldNeverReopenMinimizedChat =
      getStorageItem(STORAGE_KEY.MINIMIZED_CHAT) &&
      shouldReopenMinimizedChat === ReopenMinimizedChatState.NEVER;

    if (
      !isOpen &&
      getStorageItem(STORAGE_KEY.SESSION_ID) &&
      !getSessionStorage(STORAGE_KEY.TAB_SESSION_ID) &&
      !shouldNeverReopenMinimizedChat
    ) {
      props.openChat();
    }

    if (
      isOpen &&
      shouldReopenMinimizedChat === ReopenMinimizedChatState.ALWAYS
    ) {
      setStorageItem(STORAGE_KEY.MINIMIZED_CHAT, false);
    }
  }, [isOpen]);

  useEffect(() => {
    window.addEventListener('storage', listenLocalStorage);
    window.addEventListener('online', restoreConnection);

    return () => {
      window.removeEventListener('storage', listenLocalStorage);
      window.removeEventListener('online', restoreConnection);
    };
  }, [isOpen]);

  if (isPreviewMode) {
    const noOp = () => {};
    const previewProps = {
      ...newProps,
      openChat: noOp,
      openChatBox: noOp,
      minimizeChatBox: noOp,
      closeChatBox: noOp,
    };
    return (
      <>
        <ChatWidget {...previewProps} />
        <BubbleWidget {...previewProps} />
      </>
    );
  }

  const Component = isOpen && !isMinimized ? ChatWidget : BubbleWidget;

  return <Component {...newProps} />;
}

const mapStateToProps = state => ({
  isChatDisabled: selectors.getIsChatDisabled(state),
  isDialogEnded: selectors.getIsDialogEnded(state),
  isOpen: selectors.getIsOpen(state),
  isMinimized: selectors.getIsMinimized(state),
  isLoaded: selectors.getIsLoaded(state),
  isStarting: selectors.getIsStarting(state),
  isAsking: selectors.getIsAsking(state),
  isRetrying: selectors.getIsRetrying(state),
  hasError: selectors.getHasError(state) || hasError(state),
  thread: selectors.getThread(state),
  hasThread: selectors.getHasThread(state),
  withAgent: selectors.getWithAgent(state),
  matchFeedbackType: selectors.getMatchFeedbackType(state),
  attachmentsConfig: selectors.getAttachmentsConfig(state),
  isPreviewMode: session_selectors.getIsPreviewMode(state),
  disableSecuredStyles: session_selectors.getIsDisabledSecuredStyles(state),
  isRestartEnabled: session_selectors.getIsRestartEnabled(state),
  welcomeForm: session_selectors.getWelcomeForm(state),
  feedback: session_selectors.getFeedback(state),
  title: session_selectors.getChatTitle(state),
  subtitle: session_selectors.getChatSubtitle(state),
  font: session_selectors.getFont(state),
  footer: session_selectors.getFooter(state),
  enableTranscriptDownload: session_selectors.getTranscriptDownload(state),
  backgroundColor: session_selectors.getBackgroundColor(state),
  notificationsConfig: session_selectors.getNotificationsConfig(state),
  agent: session_selectors.getAgentData(state),
  bot: session_selectors.getBotData(state),
  shouldReopenMinimizedChat: session_selectors.getReopenMinimizedChat(state),
  isAgentConnecting: selectors.getIsAgentConnecting(state),
  isSensitiveInput: selectors.getIsSensitiveInput(state),
});

const mapDispatchToProps = dispatch => ({
  openChat: bindActionCreators(actions.openChat, dispatch),
  openChatBox: bindActionCreators(actions.openChatBox, dispatch),
  startChat: bindActionCreators(actions.startChat, dispatch),
  sendMessage: bindActionCreators(actions.sendTextMessage, dispatch),
  sendFile: bindActionCreators(actions.sendFileMessage, dispatch),
  sendTypingOn: bindActionCreators(actions.sendTypingOnMessage, dispatch),
  sendTypingOff: bindActionCreators(actions.sendTypingOffMessage, dispatch),
  openWS: bindActionCreators(actions.openWS, dispatch),
  minimizeChatBox: bindActionCreators(actions.minimizeChatBox, dispatch),
  closeChatBox: bindActionCreators(actions.closeChatBox, dispatch),
  restartChat: bindActionCreators(actions.restartChat, dispatch),
  updateThreadMessage: bindActionCreators(
    actions.updateThreadMessage,
    dispatch,
  ),
  resendMessage: bindActionCreators(actions.resendMessage, dispatch),
  restoreConnection: bindActionCreators(actions.restoreConnection, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Chat);
